import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import {
  Input, DatePicker, Space, Select,
} from "antd";
import debounce from "lodash/debounce";
import { toast } from "react-toastify";
import { formatAntdDateRange } from "../../../utils/helpers";
import { defaultDateRange } from "./utils";
import { getReasons } from "../../../actions/reportDelay";
import { fetchTestCategory } from "../../../actions/chemistry";
import { fetchQCAnalystUsers } from "../../../actions/qc";
import "./ReportDelayFilters.css";

const { RangePicker } = DatePicker;
const defaultCategoryOption = { value: "", label: "All Categories" };
const defaultAssignTo = { value: "", label: "All Reported By" };
const SEARCH_DEBOUNCE_MS = 0;

export default function ReportDelayFilters({
  onFiltersChange,
  initialFilters,
  filterVisibility,
  enableInitialInputFocus = false,
}) {
  const isMounted = useRef(true);
  const inputRef = useRef(null);
  const hasMountedRef = useRef(false);

  const [testCategories, setTestCategories] = useState([defaultCategoryOption]);
  const [assignToList, setAssignToList] = useState([defaultAssignTo]);
  const [reasonList, setReasonList] = useState([]);

  const [filters, setFilters] = useState(() => ({
    search: "",
    dateRange: defaultDateRange(),
    status: "pending",
    selectedCategory: "",
    selectedAssignTo: "",
    reason: "",
    ...(initialFilters || {}),
  }));
  const [searchInput, setSearchInput] = useState(filters.search ?? "");

  const isVisible = useCallback((key) => {
    if (Object.hasOwn(filterVisibility || {}, key)) {
      return filterVisibility[key];
    }
    return true;
  }, [filterVisibility]);

  const toApiParams = useCallback((nextFilters) => {
    const {
      search,
      dateRange,
      status,
      selectedAssignTo,
      selectedCategory,
      reason,
    } = nextFilters || {};

    const trimmed = String(search ?? "").trim();
    return {
      date_range: dateRange ? formatAntdDateRange(dateRange) : null,
      status,
      user_id: selectedAssignTo === "" ? null : selectedAssignTo,
      reason: reason === "" ? null : reason,
      test_category_id: selectedCategory === "" ? null : selectedCategory,
      search: trimmed === "" ? null : trimmed,
    };
  }, []);

  const setNextFilters = useCallback((partial) => {
    setFilters((prev) => ({ ...prev, ...partial }));
  }, []);

  const debouncedSetSearch = useMemo(() => debounce((value) => {
    setNextFilters({ search: value });
  }, SEARCH_DEBOUNCE_MS), [setNextFilters]);

  const applySearchNow = useCallback(() => {
    debouncedSetSearch.cancel();
    setNextFilters({ search: searchInput });
  }, [searchInput, debouncedSetSearch, setNextFilters]);

  const handleDateChange = (value) => {
    const dateArray = value || defaultDateRange();
    setNextFilters({ dateRange: dateArray });
  };

  const reasonOptions = useMemo(() => [
    { value: "", label: "All" },
    ...reasonList.map((item) => ({ value: item.name, label: item.name })),
  ], [reasonList]);

  const fetchTestCategories = useCallback(async () => {
    const { success, results, message } = await fetchTestCategory();
    if (!isMounted.current) return;
    if (success) {
      const newCategoryList = [
        defaultCategoryOption,
        ...(results ?? []).map((item) => {
          const { id, test_category_name } = item || {};
          return { value: id, label: test_category_name };
        }),
      ];
      setTestCategories(newCategoryList);
    } else {
      toast.error(message);
    }
  }, []);

  const fetchQCAnalystList = useCallback(async () => {
    const response = await fetchQCAnalystUsers({ role: null });
    if (!isMounted.current) return;
    if (response && response.success) {
      const result = [
        defaultAssignTo,
        ...(response.results ?? []).map((item) => ({
          value: item.id,
          label: `${item.first_name} ${item.last_name}`.trim(),
        })),
      ];
      setAssignToList(result);
    } else {
      toast.error(response?.message);
    }
  }, []);

  useEffect(() => {
    if (inputRef.current && enableInitialInputFocus) {
      inputRef.current.focus();
    }
  }, [enableInitialInputFocus]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    onFiltersChange(toApiParams(filters));
  }, [filters]); // eslint-disable-line

  useEffect(() => {
    isMounted.current = true;
    const tasks = [];
    if (isVisible("reportedBy")) {
      tasks.push(fetchQCAnalystList());
    }
    if (isVisible("testCategory")) {
      tasks.push(fetchTestCategories());
    }
    if (isVisible("reason")) {
      tasks.push(
        getReasons().then((res) => {
          if (!isMounted.current) return;
          if (res.success) {
            setReasonList(res.data ?? []);
          } else {
            toast.error(res.message || "Failed to load delay reasons");
          }
        }),
      );
    }
    Promise.all(tasks);
    return () => {
      isMounted.current = false;
    };
  }, []); //eslint-disable-line

  return (
    <Space className="ReportDelayFilterBar">
      {isVisible("search") && (
        <div className="ReportDelayFilterField">
          <label>Search</label>
          <Input.Search
            ref={inputRef}
            allowClear
            placeholder="Search by WO, Genista ID, Test"
            className="ReportDelayFilterSearch"
            value={searchInput}
            onChange={(e) => {
              const next = e.target.value;
              const hasTextPrev = searchInput.trim().length > 0;
              setSearchInput(next);
              if (hasTextPrev && next.length === 0) {
                debouncedSetSearch(next);
              }
            }}
            onPressEnter={applySearchNow}
            onSearch={(value, _, info) => {
              if (info?.source === "clear") return;
              applySearchNow();
            }}
          />
        </div>
      )}
      {isVisible("dateRange") && (
        <div className="ReportDelayFilterField">
          <label>Workorder Date Range</label>
          <RangePicker
            allowClear={false}
            className="ReportDelayFilterDate"
            format="MM/DD/YYYY"
            value={filters.dateRange}
            onChange={handleDateChange}
          />
        </div>
      )}
      {isVisible("testCategory") && (
        <div className="ReportDelayFilterField">
          <label>Test Category</label>
          <Select
            allowClear
            showSearch
            className="ReportDelayFilterSelect"
            popupClassName="ReportDelayFilterSelect__Menu"
            options={testCategories}
            value={filters.selectedCategory}
            onSelect={(v) => setNextFilters({ selectedCategory: v })}
            onClear={() => setNextFilters({ selectedCategory: "" })}
            filterOption={(inputValue, option) => option.label?.toLowerCase().includes(inputValue?.toLowerCase())}
            listHeight={400}
          />
        </div>
      )}
      {isVisible("reportedBy") && (
        <div className="ReportDelayFilterField">
          <label>Reported by</label>
          <Select
            allowClear
            showSearch
            className="ReportDelayFilterSelect"
            popupClassName="ReportDelayFilterSelect__Menu"
            options={assignToList}
            value={filters.selectedAssignTo}
            onSelect={(v) => setNextFilters({ selectedAssignTo: v })}
            onClear={() => setNextFilters({ selectedAssignTo: "" })}
            filterOption={(inputValue, option) => option.label?.toLowerCase().includes(inputValue?.toLowerCase())}
            listHeight={400}
          />
        </div>
      )}
      {isVisible("reason") && (
        <div className="ReportDelayFilterField">
          <label>Reason</label>
          <Select
            className="ReportDelayFilterSelect"
            popupClassName="ReportDelayFilterSelect__Menu"
            options={reasonOptions}
            value={filters.reason}
            onSelect={(v) => setNextFilters({ reason: v })}
          />
        </div>
      )}
      {isVisible("status") && (
        <div className="ReportDelayFilterField">
          <label>Status</label>
          <Select
            className="ReportDelayFilterSelect"
            popupClassName="ReportDelayFilterSelect__Menu"
            options={[
              { value: "pending", label: "Pending" },
              { value: "history", label: "History" },
            ]}
            value={filters.status}
            onSelect={(v) => setNextFilters({ status: v })}
          />
        </div>
      )}
    </Space>
  );
}
